import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_COLUMNS = {
  tickets: [
    { key: 'subject', label: 'Subject', type: 'text' },
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'priority', label: 'Priority', type: 'select', options: ['High', 'Medium', 'Low'] },
    { key: 'status', label: 'Status', type: 'select', options: ['Open', 'In Progress', 'Resolved', 'Closed'] },
    { key: 'assigned_to', label: 'Assigned To', type: 'text' },
  ],
  clients: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'company', label: 'Company', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
  ],
  assets: [
    { key: 'name', label: 'Asset Name', type: 'text' },
    { key: 'type', label: 'Type', type: 'select', options: ['Laptop', 'Desktop', 'Server', 'Network', 'Printer', 'Other'] },
    { key: 'serial_number', label: 'Serial #', type: 'text' },
    { key: 'assigned_to', label: 'Assigned To', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Maintenance', 'Retired'] },
  ],
  projects: [
    { key: 'name', label: 'Project Name', type: 'text' },
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Planning', 'Active', 'On Hold', 'Completed'] },
    { key: 'due_date', label: 'Due Date', type: 'date' },
    { key: 'budget', label: 'Budget', type: 'text' },
  ],
  invoices: [
    { key: 'invoice_number', label: 'Invoice #', type: 'text' },
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'amount', label: 'Amount', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Draft', 'Sent', 'Paid', 'Overdue'] },
    { key: 'due_date', label: 'Due Date', type: 'date' },
  ],
  technicians: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'specialty', label: 'Specialty', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Available', 'On Job', 'Off Duty'] },
  ],
  cyber_audit: [
    { key: 'name', label: 'Assessment Name', type: 'text' },
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'risk_level', label: 'Risk Level', type: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
    { key: 'status', label: 'Status', type: 'select', options: ['Scheduled', 'In Progress', 'Complete'] },
    { key: 'score', label: 'Score', type: 'text' },
  ],
  // Generic fallbacks for other modules
  appointments: [
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'time', label: 'Time', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Scheduled', 'Completed', 'Cancelled'] },
  ],
  staff: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'role', label: 'Role', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
  ],
  services: [
    { key: 'name', label: 'Service Name', type: 'text' },
    { key: 'category', label: 'Category', type: 'text' },
    { key: 'price', label: 'Price', type: 'text' },
    { key: 'duration', label: 'Duration', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
  ],
  payments: [
    { key: 'client', label: 'Client', type: 'text' },
    { key: 'amount', label: 'Amount', type: 'text' },
    { key: 'method', label: 'Method', type: 'select', options: ['Cash', 'Card', 'Transfer', 'Other'] },
    { key: 'status', label: 'Status', type: 'select', options: ['Completed', 'Pending', 'Refunded'] },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  reports: [
    { key: 'name', label: 'Report Name', type: 'text' },
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'period', label: 'Period', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Generated', 'Draft'] },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  marketing: [
    { key: 'name', label: 'Campaign', type: 'text' },
    { key: 'type', label: 'Type', type: 'select', options: ['Email', 'SMS', 'Social'] },
    { key: 'audience', label: 'Audience', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Draft', 'Active', 'Completed'] },
    { key: 'date', label: 'Date', type: 'date' },
  ],
};

const STATUS_COLORS = {
  'Open': '#3b82f6', 'In Progress': '#f59e0b', 'Resolved': '#22c55e', 'Closed': '#6b7280',
  'Active': '#22c55e', 'Inactive': '#ef4444', 'Maintenance': '#f59e0b', 'Retired': '#6b7280',
  'High': '#ef4444', 'Medium': '#f59e0b', 'Low': '#22c55e', 'Critical': '#dc2626',
  'Planning': '#3b82f6', 'On Hold': '#f59e0b', 'Completed': '#22c55e', 'Complete': '#22c55e',
  'Draft': '#6b7280', 'Sent': '#3b82f6', 'Paid': '#22c55e', 'Overdue': '#ef4444',
  'Available': '#22c55e', 'On Job': '#f59e0b', 'Off Duty': '#6b7280',
  'Scheduled': '#3b82f6',
};

function getDefaultColumns(moduleKey) {
  return MODULE_COLUMNS[moduleKey] || [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Inactive'] },
  ];
}

export default function CRMModule({ moduleKey, moduleName, tenantId, themeColor }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const columns = getDefaultColumns(moduleKey);
  const accent = themeColor || '#3b82f6';

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/modules/${tenantId}/${moduleKey}`, { params: { search } });
      setItems(res.data);
    } catch { /* empty */ }
    setLoading(false);
  }, [tenantId, moduleKey, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => {
    setEditingItem(null);
    setFormData({});
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData(item.data || {});
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/modules/${tenantId}/${moduleKey}/${editingItem.id}`, { data: formData });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/modules/${tenantId}/${moduleKey}`, { data: formData });
        toast.success('Created successfully');
      }
      setShowForm(false);
      fetchItems();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${moduleName.replace(/s$/, '')}?`)) return;
    try {
      await axios.delete(`${API}/modules/${tenantId}/${moduleKey}/${item.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="crm-module" data-testid={`crm-module-${moduleKey}`}>
      {/* Header */}
      <div className="crm-module-header">
        <h2>{moduleName}</h2>
        <div className="crm-module-actions">
          <div className="crm-search">
            <Search size={16} />
            <input
              type="text"
              placeholder={`Search ${moduleName.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={`crm-search-${moduleKey}`}
            />
          </div>
          <button className="crm-add-btn" style={{ background: accent }} onClick={openNew} data-testid={`crm-add-${moduleKey}`}>
            <Plus size={16} /> Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="crm-table-wrap">
        {loading ? (
          <div className="crm-loading"><Loader2 size={24} className="animate-spin" /> Loading...</div>
        ) : items.length === 0 ? (
          <div className="crm-empty" data-testid={`crm-empty-${moduleKey}`}>
            <p>No {moduleName.toLowerCase()} found.</p>
            <button className="crm-add-btn" style={{ background: accent }} onClick={openNew}>
              <Plus size={16} /> Add your first {moduleName.replace(/s$/, '').toLowerCase()}
            </button>
          </div>
        ) : (
          <table className="crm-table" data-testid={`crm-table-${moduleKey}`}>
            <thead>
              <tr>
                {columns.map(col => <th key={col.key}>{col.label}</th>)}
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {columns.map(col => (
                    <td key={col.key}>
                      {(col.key === 'status' || col.key === 'priority' || col.key === 'risk_level') ? (
                        <span className="crm-badge" style={{
                          background: `${STATUS_COLORS[item.data?.[col.key]] || '#6b7280'}18`,
                          color: STATUS_COLORS[item.data?.[col.key]] || '#6b7280'
                        }}>
                          {item.data?.[col.key] || '—'}
                        </span>
                      ) : (
                        item.data?.[col.key] || '—'
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="crm-row-actions">
                      <button className="crm-icon-btn" onClick={() => openEdit(item)} title="Edit"><Pencil size={15} /></button>
                      <button className="crm-icon-btn delete" onClick={() => handleDelete(item)} title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="crm-count">{items.length} {moduleName.toLowerCase()} total</div>

      {/* Form Modal */}
      {showForm && (
        <div className="crm-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="crm-modal" onClick={e => e.stopPropagation()} data-testid={`crm-form-${moduleKey}`}>
            <div className="crm-modal-header">
              <h3>{editingItem ? 'Edit' : 'New'} {moduleName.replace(/s$/, '')}</h3>
              <button onClick={() => setShowForm(false)} className="crm-icon-btn"><X size={18} /></button>
            </div>
            <div className="crm-modal-body">
              {columns.map(col => (
                <div key={col.key} className="crm-form-field">
                  <label>{col.label}</label>
                  {col.type === 'select' ? (
                    <select
                      value={formData[col.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type={col.type === 'date' ? 'date' : 'text'}
                      value={formData[col.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.value }))}
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="crm-modal-footer">
              <button className="crm-btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="crm-btn-save" style={{ background: accent }} onClick={handleSave} data-testid={`crm-save-${moduleKey}`}>
                {editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
